import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { getIsMobileApp } from '../../utils/mobileApp';

interface Banner {
  id: number;
  text: string;
  bgColor: string;
  textColor: string;
  linkUrl?: string | null;
  promoCode?: string | null;
  authTarget: 'All' | 'LoggedIn' | 'LoggedOut';
  targetTier?: string | null;
}

interface Subscription {
  productTier: string | null;
  isActive: boolean;
}

const getBaseUrl = (): string => {
  return process.env.REACT_APP_DFS_MARKETING_API || '';
};

// CSS custom property exposing the rendered banner height. Fixed headers and
// full-viewport-height layouts read it so they can make room for the banner.
const BANNER_HEIGHT_VAR = '--app-banner-height';
export const BANNER_HEIGHT_CSS = `var(${BANNER_HEIGHT_VAR}, 0px)`;

const setBannerHeight = (px: number) => {
  document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${px}px`);
};

export default function BannerManager() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const [banner, setBanner] = useState<Banner | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Only mobile web has the fixed ToolbarDrawer AppBar the banner must sit above.
  // Desktop and the native app WebView keep the banner in normal flow.
  const stickOnMobileWeb = !getIsMobileApp();

  // Publish the banner's height (and keep it current when the text wraps on
  // narrow screens) so the rest of the app can offset itself.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!banner || !el) return;

    // offsetHeight (not getBoundingClientRect) on purpose: App.tsx applies
    // `zoom` to <body> at tablet widths, and the consumers of this variable
    // live inside that zoomed subtree, so they need unzoomed layout pixels.
    let published = -1;
    const update = () => {
      const height = el.offsetHeight;
      if (height === published) return;
      published = height;
      setBannerHeight(height);
    };
    update();

    // Re-measure when the text re-wraps (e.g. rotation); fall back to the
    // window resize event where ResizeObserver is unavailable. The write is
    // deferred to the next frame: publishing the height re-lays out the page
    // (AppBar offset, full-height roots, scrollbar), and doing that
    // synchronously inside the observer callback trips Chrome's
    // "ResizeObserver loop completed with undelivered notifications" error.
    let frame = 0;
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleUpdate) : null;
    if (observer) {
      observer.observe(el);
    } else {
      window.addEventListener('resize', scheduleUpdate);
    }

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      setBannerHeight(0);
    };
  }, [banner]);

  useEffect(() => {
    // Fetch active banner for stokastic-tools
    const baseUrl = getBaseUrl();
    if (!baseUrl) return;
    if (isLoading) return;

    let cancelled = false;

    async function loadBanner() {
      try {
        // Delay before showing banner to avoid flash on page load
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (cancelled) return;

        const res = await fetch(`${baseUrl}/api/banners/active?app=stokastic-tools`);
        if (!res.ok) return;

        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) return;

        const data: Banner | null = await res.json();
        if (!data || cancelled) return;

        // Check per-banner dismissal
        if (localStorage.getItem(`banner_dismissed_${data.id}`) === 'true') return;

        // Client-side auth filtering
        if (data.authTarget === 'LoggedIn' && !isAuthenticated) return;
        if (data.authTarget === 'LoggedOut' && isAuthenticated) return;

        // Tier targeting check
        if (data.targetTier && isAuthenticated) {
          try {
            const token = await getAccessTokenSilently();
            const tierRes = await fetch(`${baseUrl}/api/subscriptions/my-tier`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (tierRes.ok) {
              const subs: Subscription[] = await tierRes.json();
              const hasTier = subs.some(
                (s) => s.isActive && s.productTier?.toLowerCase() === data.targetTier?.toLowerCase()
              );
              if (!hasTier) return;
            } else {
              return;
            }
          } catch {
            return;
          }
        } else if (data.targetTier && !isAuthenticated) {
          return;
        }

        if (!cancelled) {
          setBanner(data);
        }
      } catch {
        // Fetch failed silently
      }
    }

    loadBanner();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  const handleDismiss = () => {
    if (banner) {
      localStorage.setItem(`banner_dismissed_${banner.id}`, 'true');
    }
    setBanner(null);
  };

  if (!banner) return null;

  return (
    <Box
      ref={rootRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: banner.bgColor,
        py: 1,
        // Phone widths: reserve room on both sides so wrapped text never runs
        // under the close button.
        px: { xs: 6, sm: 2 },
        // Mobile web, phone widths only: the tool header is a fixed AppBar, so
        // stick the banner to the top above it (the AppBar offsets itself by
        // BANNER_HEIGHT_CSS) so the two never overlap and the banner stays
        // visible while scrolling. Everywhere else the banner stays in flow.
        position: stickOnMobileWeb ? { xs: 'sticky', sm: 'relative' } : 'relative',
        top: 0,
        zIndex: stickOnMobileWeb ? { xs: theme.zIndex.appBar + 1, sm: 'auto' } : 'auto',
      }}
    >
      <Box
        component={banner.linkUrl ? 'a' : 'div'}
        href={banner.linkUrl || undefined}
        target={banner.linkUrl ? '_blank' : undefined}
        rel={banner.linkUrl ? 'noopener noreferrer' : undefined}
        sx={{
          textDecoration: 'none',
          cursor: banner.linkUrl ? 'pointer' : 'default',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Public Sans", sans-serif',
            fontSize: 14,
            fontWeight: 600,
            color: banner.textColor,
            textAlign: 'center',
          }}
        >
          {banner.text}
          {banner.promoCode && (
            <Box
              component="span"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                px: 0.75,
                py: 0.25,
                borderRadius: '4px',
                fontWeight: 700,
                ml: 1,
              }}
            >
              {banner.promoCode}
            </Box>
          )}
        </Typography>
      </Box>
      <IconButton
        onClick={handleDismiss}
        size="small"
        sx={{
          position: 'absolute',
          right: 8,
          color: banner.textColor,
          opacity: 0.7,
          '&:hover': { opacity: 1 },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
